<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import { boxBounds } from '@vp/core';
import { live, ui } from '../editor/bridge';
import { profileCard } from '../profile-card';
import { displayName, listProfiles, type ProfileRecord } from '../profiles/registry';
import ProfileAvatar from './ProfileAvatar.vue';

/**
 * Who added the object under the pointer. The name is stored on the object; older objects
 * belong to the open profile. The picture comes from a profile on this device, when it is here.
 */
const known = ref(new Map<string, ProfileRecord>());
onMounted(async () => {
  const list = await listProfiles().catch(() => [] as ProfileRecord[]);
  known.value = new Map(list.map((p) => [p.id, p]));
});

const tip = computed(() => {
  void ui.camRev;
  void ui.docRev;
  if (ui.dragging || !ui.hover) return null;
  const o = live.editor?.doc.get(ui.hover);
  const cam = live.renderer?.camera;
  if (!o || !cam) return null;
  const stored = o.addedBy;
  const by = stored ?? (profileCard.id ? { id: profileCard.id, firstName: profileCard.firstName, lastName: profileCard.lastName } : null);
  if (!by) return null;
  const name = by.id === profileCard.id ? displayName(profileCard) : displayName({ firstName: by.firstName, lastName: by.lastName ?? '' });
  if (!name) return null;
  const b = boxBounds(o);
  const p = cam.worldToScreen(b.x + b.w / 2, b.y);
  const avatar = by.id === profileCard.id ? profileCard.avatar : (known.value.get(by.id)?.avatar ?? '');
  return { x: p.x, y: Math.max(8, p.y), name, id: by.id, firstName: by.firstName, lastName: by.lastName ?? '', avatar };
});
</script>

<template>
  <div v-if="tip" class="author" :style="{ left: tip.x + 'px', top: tip.y + 'px' }" data-author-pill>
    <ProfileAvatar :id="tip.id" :first-name="tip.firstName" :last-name="tip.lastName" :avatar="tip.avatar" :size="18" />
    <span class="name">{{ tip.name }}</span>
  </div>
</template>

<style scoped>
.author {
  position: absolute;
  z-index: 7;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: 240px;
  height: 26px;
  padding: 0 8px 0 4px;
  border: 1px solid var(--border);
  border-radius: var(--radius-control);
  background: var(--panel);
  box-shadow: var(--shadow);
  color: var(--text);
  font-size: 12px;
  pointer-events: none;
  transform: translate(-50%, -100%) translateY(-8px);
}
.name {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
</style>
